import React, { Suspense, useEffect } from "react";
import styled from "styled-components";
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import { ADMIN_API_URL } from "./config";
// i18n bootstrap (must load before any component calls useTranslation).
import "./i18n";
// Layout chrome is needed immediately, so it stays in the main bundle.
import Footer from "./Components/Footer";
import NavBar from "./Components/NavBar";
import JobLanguageSync from "./Components/JobLanguageSync";
import { JobLangProvider } from "./i18n/JobLangContext";

// ── Global redirect-wallet self-heal ──────────────────────────────────────────
// Amazon Pay / Cash App and other redirect payment methods send the buyer to the bank
// and back. On the way back the payment widget that would finalize the purchase inline
// is usually NOT mounted (it lives behind a paywall toggle), so the entitlement never
// got applied even though Stripe charged the card. Stripe appends
// ?payment_intent=…&redirect_status=succeeded to the return URL — so a SINGLE handler at
// the app root can recover EVERY offering: POST the intent to the generic reconcile
// endpoint (it derives the offering + resume from the PI metadata and is idempotent),
// then strip the params so a refresh can't re-fire it. This closes the redirect-wallet
// gap for every <OfferingPayment> paywall at once, not just the docx download.
const RECONCILE_URL = `${ADMIN_API_URL}/api-recruitment-admin/boost-subscription/payment-element/reconcile`;
const useWalletRedirectReconcile = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get("payment_intent");
    if (!paymentIntentId || params.get("redirect_status") !== "succeeded") return;
    let cancelled = false;
    (async () => {
      // Keep the params for a retry ONLY on a transient failure (network / 5xx). A 200 (applied or
      // "nothing to apply") or a 4xx (this PI can't be reconciled by us) is final — strip and move on.
      let retryable = false;
      try {
        const res = await fetch(RECONCILE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentIntentId }),
        });
        retryable = res.status >= 500;
      } catch {
        retryable = true; // network error — let a refresh try again
      }
      if (cancelled || retryable) return;
      // Strip Stripe's return params so a refresh won't re-run this and the URL stays clean.
      params.delete("payment_intent");
      params.delete("payment_intent_client_secret");
      params.delete("redirect_status");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    })();
    return () => { cancelled = true; };
  }, []);
};

// Route components are code-split (lazy) so each candidate page only ships the
// JS it needs. This dramatically shrinks the initial bundle (one big chunk ->
// per-route chunks), cutting unused JS, main-thread work, and LCP/TBT.
const JobDetails = React.lazy(() => import("./Job/JobDetails"));
const Experiences = React.lazy(() => import("./Job/Experiences"));
const References = React.lazy(() => import("./Job/References"));
const QuestionnaireStandalone = React.lazy(() => import("./Job/QuestionnaireStandalone"));
const VideoQuestionnaire = React.lazy(() => import("./Job/VideoQuestionnaire"));
const VideoQuestionnaireStandalone = React.lazy(() => import("./Job/VideoQuestionnaireStandalone"));
const PrivacyPolicy = React.lazy(() => import("./Components/PrivacyPolicy"));
const TermsAndConditions = React.lazy(() => import("./Components/TermsAndConditions"));
const ThankYouSecond = React.lazy(() => import("./Job/ThankYouSecond"));
const BoostPayment = React.lazy(() => import("./Job/BoostPayment"));
const UnsubscribeBoost = React.lazy(() => import("./Job/UnsubscribeBoost"));
const ViewAIScoreUnlock = React.lazy(() => import("./Job/ViewAIScoreUnlock"));
const ViewAIScoreArchive = React.lazy(() => import("./Job/ViewAIScoreArchive"));
const ViewAIHireScoreFree = React.lazy(() => import("./Job/ViewAIHireScoreFree"));
const AIHireScoreRequestReceived = React.lazy(() => import("./Job/AIHireScoreRequestReceived"));
const ResumeRewriteUnlock = React.lazy(() => import("./Job/ResumeRewriteUnlock"));
const C2CRateConfirm = React.lazy(() => import("./Job/C2CRateConfirm"));
const C2CEnterEmployer = React.lazy(() => import("./Job/C2CEnterEmployer"));
const RTRIncompleteReason = React.lazy(() => import("./Job/RTRIncompleteReason"));
const IntroVideoCapture = React.lazy(() => import("./Job/IntroVideoCapture"));
const CandidateInsights = React.lazy(() => import("./Job/CandidateInsights"));
// import DocuSign from "./Job/DocuSign";

const AppContent = () => {
  useWalletRedirectReconcile();
  return (
    <JobLangProvider>
    <PageWrapper>
      <JobLanguageSync />
      <NavBar />
      <BodyWrap>
        <Suspense fallback={<LoadingFallback>Loading…</LoadingFallback>}>
          <Routes>
            <Route path="candidate/job/:jobId" element={<JobDetails />} />
            {/* Mock Interview (video, paywall/scoring/share) — NOT the recruiter video questionnaire */}
            <Route path="candidate/job/:jobId/:resumeId/video-form" element={<VideoQuestionnaire />} />
            {/* Recruiter-invited VIDEO questionnaire (twin of the text questionnaire; no mock-interview bits) */}
            <Route path="candidate/job/:jobId/:resumeId/video-questionnaire" element={<VideoQuestionnaireStandalone />} />
            {/* Non-DIR job text questionnaire route (fallback from video form) */}
            <Route path="candidate/job/:jobId/:resumeId/form" element={<QuestionnaireStandalone />} />
            {/* DIR job flow routes */}
            <Route path="candidate/job/:jobId/:resumeId/experiences" element={<Experiences />} />
            <Route path="candidate/job/:jobId/:resumeId/questionnaire" element={<QuestionnaireStandalone />} />
            <Route path="candidate/job/:jobId/:resumeId/references" element={<References />} />
            <Route path="candidate/job/:jobId/:resumeId/thank-you" element={<ThankYouSecond />} />
            <Route path="candidate/job/:jobId/:resumeId/boost" element={<BoostPayment />} />
            <Route path="candidate/job/:jobId/:resumeId/ai-score-unlock" element={<ViewAIScoreUnlock />} />
            <Route path="candidate/job/:jobId/:resumeId/ai-score-archive" element={<ViewAIScoreArchive />} />
            {/* Free, compliant candidate insights page (post-close email entry point) */}
            <Route path="candidate/job/:jobId/:resumeId/insights" element={<CandidateInsights />} />
            <Route path="candidate/job/:jobId/:resumeId/ai-resume-rewrite" element={<ResumeRewriteUnlock />} />
            <Route path="candidate/job/:jobId/:resumeId/ai-hire-score" element={<ViewAIHireScoreFree />} />
            <Route path="candidate/job/:jobId/:resumeId/ai-hire-score/confirmation" element={<AIHireScoreRequestReceived />} />
            {/* Public, token-gated employer C2C rate-confirmation page (from email) */}
            <Route path="candidate/job/:jobId/:resumeId/c2c-confirm" element={<C2CRateConfirm />} />
            {/* Candidate "enter your employer's details" page (from the C2C reminder email) */}
            <Route path="candidate/job/:jobId/:resumeId/c2c-employer" element={<C2CEnterEmployer />} />
            {/* "Why didn't you finish the RTR?" reason capture (from reminder email) */}
            <Route path="candidate/job/:jobId/:resumeId/rtr-incomplete-reason" element={<RTRIncompleteReason />} />
            {/* Profile intro: record a short intro video + capture a headshot */}
            <Route path="candidate/job/:jobId/:resumeId/intro" element={<IntroVideoCapture />} />
            <Route path="candidate/boost/unsubscribe" element={<UnsubscribeBoost />} />
            <Route path="/candidate/privacy" element={<PrivacyPolicy />} />
            <Route path="/candidate/terms" element={<TermsAndConditions />} />
          </Routes>
        </Suspense>
      </BodyWrap>
      <FooterWrap>
        <Footer />
      </FooterWrap>
    </PageWrapper>
    </JobLangProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const BodyWrap = styled.article`
  background-color: #faf9f6;
  padding: 40px;
  flex: 1;

  /* On phones the candidate pages render full-bleed (width: 100vw) and supply
     their own inner padding — so the wrapper must not add side padding. */
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const LoadingFallback = styled.div`
  padding: 40px;
  text-align: center;
  color: #5f6b7a;
  font-size: 16px;
`;

const FooterWrap = styled.footer`
  background-color:  #2E7D32;
  color: white;
  padding: 20px;
`;

export default App;
